import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Repeat, Pause, Play, Trash2, CalendarClock, Loader2 } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { Toast } from "./Toast";

const FREQ_OPTIONS = [
  { weeks: 4, label: "Every 4 weeks" },
  { weeks: 6, label: "Every 6 weeks" },
  { weeks: 8, label: "Every 8 weeks" },
  { weeks: 12, label: "Every 12 weeks" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function MySubscriptions() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/subscriptions`);
      setItems(res.data?.items || []);
    } catch (err) {
      console.error("Failed to fetch subscriptions:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const update = async (id, payload, successMsg) => {
    setBusyId(id);
    try {
      await axios.put(`${API}/subscriptions/${id}`, payload);
      await fetchItems();
      setToast({ message: successMsg, type: "success" });
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Could not update", type: "error" });
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this Auto-Reorder? You can always set up a new one later.")) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/subscriptions/${id}`);
      setItems((prev) => prev.filter((s) => s.id !== id));
      setToast({ message: "Auto-Reorder cancelled", type: "success" });
    } catch (err) {
      setToast({ message: "Could not cancel", type: "error" });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="card p-6 text-center" data-testid="my-subscriptions-loading">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
      </div>
    );
  }

  return (
    <section className="card p-6" data-testid="my-subscriptions">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#00a99d]/10 flex items-center justify-center">
          <Repeat className="w-5 h-5 text-[#00a99d]" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">My Auto-Reorders</h2>
          <p className="text-sm text-gray-500">Reminder emails — never run out of essentials.</p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg" data-testid="my-subscriptions-empty">
          <CalendarClock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-600 font-medium">No Auto-Reorders yet</p>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            Set one up from any product or your Re-order list and we'll remind you on schedule.
          </p>
          <Link
            to="/products"
            className="inline-block bg-[#00a99d] hover:bg-[#008c82] text-white text-sm font-semibold px-4 py-2 rounded-lg"
          >
            Browse products
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((s) => {
            const paused = s.status === "paused";
            return (
              <div
                key={s.id}
                className={`border rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-4 ${paused ? "bg-gray-50" : "bg-white"}`}
                data-testid={`subscription-${s.id}`}
              >
                <Link to={`/products/${s.product_id}`} className="flex items-center gap-3 flex-1 min-w-0">
                  <img
                    src={s.product_image || "https://via.placeholder.com/60"}
                    alt=""
                    className="w-14 h-14 object-contain rounded bg-gray-50 flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-gray-900 line-clamp-2 hover:text-[#00a99d]">{s.product_name}</p>
                    <p className="text-xs text-gray-500">
                      {s.quantity} × £{Number(s.unit_price || 0).toFixed(2)} ·{" "}
                      <span className={paused ? "text-amber-600 font-medium" : "text-green-600 font-medium"}>
                        {paused ? "Paused" : "Active"}
                      </span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Next reminder: <strong className="text-gray-600">{paused ? "—" : formatDate(s.next_reminder_at)}</strong>
                    </p>
                  </div>
                </Link>

                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={s.frequency_weeks}
                    onChange={(e) => update(s.id, { frequency_weeks: parseInt(e.target.value) }, "Frequency updated")}
                    disabled={busyId === s.id}
                    className="text-xs border rounded-md px-2 py-1.5 bg-white"
                    data-testid={`sub-freq-${s.id}`}
                  >
                    {FREQ_OPTIONS.map((f) => (
                      <option key={f.weeks} value={f.weeks}>{f.label}</option>
                    ))}
                  </select>

                  {paused ? (
                    <button
                      onClick={() => update(s.id, { status: "active" }, "Auto-Reorder resumed")}
                      disabled={busyId === s.id}
                      className="inline-flex items-center gap-1 text-xs font-semibold bg-[#00a99d] hover:bg-[#008c82] text-white px-3 py-1.5 rounded-md"
                      data-testid={`sub-resume-${s.id}`}
                    >
                      <Play className="w-3.5 h-3.5" /> Resume
                    </button>
                  ) : (
                    <button
                      onClick={() => update(s.id, { status: "paused" }, "Auto-Reorder paused")}
                      disabled={busyId === s.id}
                      className="inline-flex items-center gap-1 text-xs font-semibold border border-gray-300 hover:border-amber-400 hover:text-amber-600 px-3 py-1.5 rounded-md"
                      data-testid={`sub-pause-${s.id}`}
                    >
                      <Pause className="w-3.5 h-3.5" /> Pause
                    </button>
                  )}

                  <button
                    onClick={() => cancel(s.id)}
                    disabled={busyId === s.id}
                    className="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-md text-gray-500 hover:border-red-400 hover:text-red-500"
                    data-testid={`sub-cancel-${s.id}`}
                    title="Cancel Auto-Reorder"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </section>
  );
}
